import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import {
  ArrowRight, Calendar, CheckCircle, Clock,
  MessageSquare, Play, Star,
  Users, Video, Zap, X, Heart, FileText, DollarSign,
} from 'lucide-react'
import './LandingPage.css'

const stepPreviews = [
  {
    n: 1,
    Icon: Calendar,
    title: 'Schedule Your Session',
    desc: 'Choose your target role, share your job description, and pick a time that works for you. We match you with an experienced interviewer.',
    preview: (
      <div className="step-preview-content">
        <div className="step-preview-icon"><Calendar size={40} /></div>
        <h3>Schedule Your Session</h3>
        <p>Tell us your target role and what you're preparing for. Pick a date and time that fits your schedule — we'll match you with an expert interviewer who specializes in your field.</p>
        <ul className="step-preview-list">
          <li><CheckCircle size={16} /> Choose target role and experience level</li>
          <li><CheckCircle size={16} /> Upload your job description (optional)</li>
          <li><CheckCircle size={16} /> Select from available time slots</li>
          <li><CheckCircle size={16} /> Secure checkout — $149 one-time payment</li>
        </ul>
      </div>
    ),
  },
  {
    n: 2,
    Icon: Video,
    title: 'Live Mock Interview',
    desc: 'Join a realistic 1-on-1 interview over video. Your interviewer tailors questions to your role and evaluates your performance in real time.',
    preview: (
      <div className="step-preview-content">
        <div className="step-preview-zoom">
          <div className="zoom-header">
            <span className="zoom-dot red" /><span className="zoom-dot yellow" /><span className="zoom-dot green" />
            <span className="zoom-title">Interview Dress Rehearsal — Live Session</span>
          </div>
          <div className="zoom-body">
            <div className="zoom-video-grid">
              <div className="zoom-participant interviewer-view">
                <div className="zoom-avatar"><Users size={32} /></div>
                <span>Your Interviewer</span>
              </div>
              <div className="zoom-participant you-view">
                <div className="zoom-avatar self"><Star size={32} /></div>
                <span>You</span>
              </div>
            </div>
            <div className="zoom-controls">
              <span className="zoom-btn">🎙 Mute</span>
              <span className="zoom-btn">📷 Video</span>
              <span className="zoom-btn active-rec">● Recording</span>
              <span className="zoom-btn end">End</span>
            </div>
          </div>
        </div>
        <p style={{ marginTop: 16, color: '#6b7280', fontSize: 14 }}>A real 1-on-1 video interview tailored to your role — just like the real thing.</p>
      </div>
    ),
  },
  {
    n: 3,
    Icon: MessageSquare,
    title: 'Get Actionable Feedback',
    desc: 'Receive a detailed feedback report with strengths, areas for improvement, and specific next steps — reviewed by your interviewer before delivery.',
    preview: (
      <div className="step-preview-content">
        <div className="step-preview-icon"><MessageSquare size={40} /></div>
        <h3>Your Feedback Report</h3>
        <div className="feedback-preview-items">
          <div className="fp-item positive"><CheckCircle size={16} /> <div><strong>Communication</strong><span>Clear and well-structured answers.</span></div></div>
          <div className="fp-item positive"><CheckCircle size={16} /> <div><strong>Problem Solving</strong><span>Strong analytical approach demonstrated.</span></div></div>
          <div className="fp-item improve"><span style={{fontSize:18}}>🎯</span> <div><strong>Improve</strong><span>Provide more specific examples and metrics.</span></div></div>
          <div className="fp-item suggestion"><span style={{fontSize:18}}>💡</span> <div><strong>Suggestion</strong><span>Practice summarizing key points.</span></div></div>
        </div>
        <p style={{ marginTop: 14, color: '#6b7280', fontSize: 13 }}>Delivered within 24 hours — reviewed and personalized by your interviewer.</p>
      </div>
    ),
  },
]

export default function LandingPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const headerRef = useRef<HTMLElement>(null)
  const [activeStep, setActiveStep] = useState<number | null>(null)

  const toggleMobileNav = () => {
    headerRef.current?.classList.toggle('nav-open')
  }

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
    headerRef.current?.classList.remove('nav-open')
  }

  useEffect(() => {
    if (activeStep === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveStep(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [activeStep])

  return (
    <div className="landing">

      {/* ── Header ── */}
      <header className="landing-header" ref={headerRef}>
        <div className="landing-container">
          <button
            type="button"
            className="header-brand header-brand-btn"
            onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
            aria-label="Go to home"
          >
            <img src="/InterviewDressRehearsal_LOGO.png" alt="Interview Dress Rehearsal" className="header-logo" />
            <div className="header-name-wrap">
              <span className="header-name-full">Interview Dress Rehearsal</span>
            </div>
          </button>

          <nav className="header-nav">
            <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollTo('how-it-works') }}>How It Works</a>
            <a href="#features"     onClick={(e) => { e.preventDefault(); scrollTo('features')     }}>Features</a>
            <a href="#become-interviewer" onClick={(e) => { e.preventDefault(); scrollTo('become-interviewer') }}>Become an Interviewer</a>
            <a href="/about-us" onClick={(e) => { e.preventDefault(); navigate('/about-us') }}>About Us</a>
            {user ? (
              <button className="btn-outline-light" onClick={() => navigate(`/${user.role}`)}>
                {user.name || user.email.split('@')[0]}
              </button>
            ) : (
              <button className="btn-outline-light" onClick={() => navigate('/signin')}>Sign In</button>
            )}
            {!user && (
              <button className="btn-cta" onClick={() => navigate('/signup')}>Get Started</button>
            )}
          </nav>

          <button className="mobile-nav-toggle" onClick={toggleMobileNav} aria-label="Toggle navigation">
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="hero">
        <div className="landing-container">
          <div className="hero-content">
            <p className="hero-intro">Interview Dress Rehearsal, a site to practice your interview answers.</p>
            <div className="hero-title-wrap">
              <img
                src="/InterviewDressRehearsal_LOGO.png"
                alt=""
                aria-hidden="true"
                className="hero-square-logo"
              />
              <h1 className="hero-title">
                Bomb here.<br />
                <span className="hero-highlight">Shine when it matters.</span>
              </h1>
            </div>
            <p className="hero-subtitle">
              Interview Dress Rehearsal gives you a real, live mock interview tailored to your
              role — plus clear feedback you can use immediately, so you walk into the real
              interview with confidence.
            </p>
            <div className="hero-actions">
              <button className="btn-cta btn-lg" onClick={() => navigate('/signup')}>
                Schedule a Session <ArrowRight size={20} />
              </button>
              <a href="#how-it-works" className="btn-ghost btn-lg" onClick={(e) => { e.preventDefault(); scrollTo('how-it-works') }}>
                <Play size={18} /> How It Works
              </a>
            </div>
            <div className="hero-trust">
              {['Live 1-on-1 sessions', 'Role-specific prep', 'Actionable feedback'].map((t) => (
                <div key={t} className="trust-item">
                  <CheckCircle size={16} /> <span>{t}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Mock interview UI card */}
          <div className="hero-visual">
            <div className="hero-card hero-card-wide">
              <div className="hero-card-header">
                <div className="hero-card-dot red"    />
                <div className="hero-card-dot yellow" />
                <div className="hero-card-dot green"  />
              </div>
              <div className="hero-card-body">

                {/* Three-column layout matching reference */}
                <div className="mock-ui-grid">

                  {/* Col 1 — Video call */}
                  <div className="mock-video-col">
                    <span className="mock-col-badge"><Video size={11} /> Zoom Video</span>
                    <div className="mock-video-frame">
                      {/* Real photo video feed */}
                      <div className="mock-video-bg">
                        <img
                          src="/zoom_meeting_ai_image.jpeg"
                          alt="Live mock interview"
                          className="mock-video-photo"
                        />
                      </div>

                    </div>
                  </div>

                  {/* Col 2 — Transcript */}
                  <div className="mock-transcript-col">
                    <span className="mock-col-badge"><FileText size={11} /> Transcript</span>
                    <div className="mock-ts-lines">
                      <div className="mock-ts-line"><span className="mock-ts">00:12</span><span>Thank you for the question.</span></div>
                      <div className="mock-ts-line"><span className="mock-ts">00:18</span><span>In my previous role, I worked on…</span></div>
                      <div className="mock-ts-line"><span className="mock-ts">00:45</span><span>The challenge was that…</span></div>
                      <div className="mock-ts-line"><span className="mock-ts">01:10</span><span>To solve this, I implemented…</span></div>
                      <div className="mock-ts-line"><span className="mock-ts">01:45</span><span>The result was…</span></div>
                    </div>
                  </div>

                  {/* Col 3 — Feedback */}
                  <div className="mock-feedback-col">
                    <span className="mock-col-badge"><MessageSquare size={11} /> Feedback</span>
                    <div className="mock-fb-items">
                      <div className="mock-fb-item mock-fb-positive">
                        <CheckCircle size={13} className="mock-fb-icon" />
                        <div><div className="mock-fb-title">Communication</div><div className="mock-fb-sub">Clear and well-structured answers.</div></div>
                      </div>
                      <div className="mock-fb-item mock-fb-positive">
                        <CheckCircle size={13} className="mock-fb-icon" />
                        <div><div className="mock-fb-title">Problem Solving</div><div className="mock-fb-sub">Strong analytical approach demonstrated.</div></div>
                      </div>
                      <div className="mock-fb-item mock-fb-improve">
                        <span className="mock-fb-emoji">🎯</span>
                        <div><div className="mock-fb-title">Improve</div><div className="mock-fb-sub">Provide more specific examples and metrics.</div></div>
                      </div>
                      <div className="mock-fb-item mock-fb-suggestion">
                        <span className="mock-fb-emoji">💡</span>
                        <div><div className="mock-fb-title">Suggestion</div><div className="mock-fb-sub">Practice summarizing key points.</div></div>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Package footer */}
                <div className="pricing-footer">
                  <div className="pricing-footer-left">
                    <div className="pricing-icon-wrap"><Video size={22} /></div>
                    <div>
                      <div className="pricing-pkg-title">Mock Interview Package</div>
                      <div className="pricing-pkg-desc">Zoom Video + Transcript + Feedback</div>
                      <div className="pricing-pkg-tag">Prepare. Practice. Succeed.</div>
                    </div>
                  </div>
                  <div className="pricing-footer-right">
                    <div className="pricing-amount"><DollarSign size={18} />149</div>
                    <div className="pricing-once">One-time payment</div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="how-it-works">
        <div className="landing-container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Three simple steps to interview confidence — click any step for more information</p>
          </div>
          <div className="steps-grid">
            {stepPreviews.map(({ n, Icon, title, desc }) => (
              <div
                key={n}
                className="step-card step-card-clickable"
                onClick={() => setActiveStep(n)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setActiveStep(n)
                  }
                }}
              >
                <div className="step-number">{n}</div>
                <div className="step-icon"><Icon size={32} /></div>
                <h3>{title}</h3>
                <p>{desc}</p>
                <div className="step-see-more">See more information →</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Step Preview Modal ── */}
      {activeStep !== null && (
        <div
          className="step-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setActiveStep(null)
          }}
        >
          <div className="step-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="step-modal-close"
              onClick={(e) => { e.stopPropagation(); setActiveStep(null) }}
              aria-label="Close"
            >
              <X size={20} />
            </button>
            {stepPreviews.find((s) => s.n === activeStep)?.preview}
          </div>
        </div>
      )}

      {/* ── Features ── */}
      <section id="features" className="features-section">
        <div className="landing-container">
          <div className="section-header">
            <h2>Why Interview Dress Rehearsal?</h2>
            <p>Everything you need to walk in confident</p>
          </div>
          <div className="features-grid">
            {[
              { Icon: Calendar,      title: 'Role-Tailored Questions',  desc: 'AI-powered question generation based on your target role, experience level, and the actual job description.' },
              { Icon: Users,         title: 'Expert Interviewers',       desc: 'Practice with experienced professionals who have conducted real interviews at top companies.' },
              { Icon: MessageSquare, title: 'Structured Feedback',       desc: 'Get clear strengths, opportunities, and practice actions — no confusing numeric scores, just guidance you can use.' },
              { Icon: Heart,         title: 'Human Reaction Feedback',   desc: 'Receive genuine, human feedback that reflects how real interviewers actually perceive your answers and presence.' },
              { Icon: Zap,           title: 'AI-Assisted Insights',      desc: 'AI drafts your feedback from session recordings, then your interviewer reviews and personalizes it before delivery.' },
              { Icon: Clock,         title: 'Quick Turnaround',          desc: 'Receive your detailed feedback report within 24 hours of your session.' },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="feature-card">
                <div className="feature-icon-wrap"><Icon size={24} /></div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Interviewer CTA ── */}
      <section id="become-interviewer" className="interviewer-cta">
        <div className="landing-container">
          <div className="cta-card">
            <div className="cta-content">
              <h2>Become an Interviewer</h2>
              <p>
                Are you an experienced professional who has conducted real interviews? Join our team of expert interviewers.
              </p>
              <ul className="cta-benefits">
                <li><Star size={16} /> Flexible scheduling — you set your availability</li>
                <li><Star size={16} /> Competitive pay by the session</li>
                <li><Star size={16} /> Make a real impact on candidates' careers</li>
              </ul>
              <button className="btn-cta btn-lg" onClick={() => navigate('/apply-interviewer')}>
                Apply Now <ArrowRight size={20} />
              </button>
            </div>
            <div className="cta-visual">
              {[
                { Icon: Star,  value: '4.9', label: 'Avg. Interviewer Rating' },
                { Icon: Users, value: '50+', label: 'Active Interviewers' },
              ].map(({ Icon, value, label }) => (
                <div key={label} className="cta-stat-card">
                  <div className="cta-stat-icon"><Icon size={24} /></div>
                  <div className="cta-stat-value">{value}</div>
                  <div className="cta-stat-label">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="final-cta">
        <div className="landing-container">
          <h2>Ready to ace your next interview?</h2>
          <p>Schedule a dress rehearsal and walk in with confidence.</p>
          <div className="final-actions">
            <button className="btn-cta btn-lg" onClick={() => navigate('/signup')}>
              Schedule a Session <ArrowRight size={20} />
            </button>
            <button className="btn-ghost-dark btn-lg" onClick={() => navigate('/signin')}>
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src="/InterviewDressRehearsal_LOGO.png" alt="Interview Dress Rehearsal" className="footer-logo" />
              <span>Interview Dress Rehearsal</span>
            </div>
            <div className="footer-links">
              <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollTo('how-it-works') }}>How It Works</a>
              <a href="#features"     onClick={(e) => { e.preventDefault(); scrollTo('features')     }}>Features</a>
              <a href="#become-interviewer" onClick={(e) => { e.preventDefault(); scrollTo('become-interviewer') }}>Become an Interviewer</a>
              <a href="/about-us" onClick={(e) => { e.preventDefault(); navigate('/about-us') }}>About Us</a>
              <a href="/signin"  onClick={(e) => { e.preventDefault(); navigate('/signin') }}>Sign In</a>
              <a href="/support" onClick={(e) => { e.preventDefault(); navigate('/support') }}>Support</a>
              <a href="/zoom-docs" onClick={(e) => { e.preventDefault(); navigate('/zoom-docs') }}>Zoom Docs</a>
              <a href="mailto:[TODO: feedback email]" className="footer-feedback-link">Send Feedback</a>
            </div>
            <p className="footer-copy">&copy; {new Date().getFullYear()} Interview Dress Rehearsal. All rights reserved.</p>
            <div className="footer-legal">
              <a href="/terms-and-conditions" onClick={(e) => { e.preventDefault(); navigate('/terms-and-conditions') }}>Terms &amp; Conditions</a>
              <span className="footer-legal-sep">·</span>
              <a href="/payment-policy" onClick={(e) => { e.preventDefault(); navigate('/payment-policy') }}>Payment Policy</a>
              <span className="footer-legal-sep">·</span>
              <a href="/privacy-policy" onClick={(e) => { e.preventDefault(); navigate('/privacy-policy') }}>Privacy Policy</a>
            </div>
          </div>
        </div>
      </footer>

    </div>
  )
}
